import * as React from 'react';

import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

import SubmitIcon from '@mui/icons-material/Publish';
import ApproveIcon from '@mui/icons-material/DoneAll';
import { yupResolver } from '@hookform/resolvers/yup';

import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useSnackbar } from 'notistack';

import ControlledForm from '../../components/ControlledForm';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import ScrollToTop from '../../components/ScrollToTop';

import FormReadOnlySection from '../../components/FormReadOnlySection';
import FormPaper from '../../components/FormPaper';
import FormTitle from '../../components/FormTitle';
import FormHeader from '../../components/FormHeader';

import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import { useDispatchEventReportData } from '../../core/context/DispatchEventReportDataContext';
import useDeleteDispatchEventDraft from '../../core/api/hooks/dispatch-event/useDeleteDispatchEventDraft';
import { EventError, DispatchEventReport } from '../../core/api/clients';

import EventStep from './FormSteps/EventStep';
import WeatherStep from './FormSteps/WeatherStep';
import NarrativeStep from './FormSteps/NarrativeStep';

import '../../core/styles/disabledFields.css';
import FlightStep from './FormSteps/FlightStep';
import FormStatus from '../../components/FormStatus';

import { IDispatchApprovalRequest } from '../../core/api/hooks/dispatch-event/useApproveDispatchEvent';
import DispatchEventFormValidationSchema from './DispatchEventFormValidationSchema';
import { FormValidationProvider } from '../../core/context/FormValidationContext';
import queryKeys from '../../core/api/queryKeys';
import NotificationStateRadioGroup, { NotificationState } from '../../components/NotificationStateRadioGroup';

function DispatchEventFormView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authorizedFormData = useAuthorizedFormData();
  const { enqueueSnackbar } = useSnackbar();
  const {
    dispatchEventReport,
    update,
    approve,
    submit,
    allowedActions,
    submitStatus,
    approveStatus,
  } = useDispatchEventReportData();

  const { mutate: deleteDispatchEventDraft } = useDeleteDispatchEventDraft();
  const [confDialogOpen, setConfDialogOpen] = React.useState<boolean>(false);
  const [notifyPartner, setNotifyPartner] = React.useState<NotificationState>();
  const formMethods = useForm<DispatchEventReport>({
    defaultValues: dispatchEventReport,
    mode: 'onBlur',
    resolver: yupResolver(DispatchEventFormValidationSchema),
  });

  React.useEffect(() => {
    formMethods.reset(dispatchEventReport);
  }, [dispatchEventReport]);

  const submitCallback = () => {
    Promise.all([
      queryClient.refetchQueries({ queryKey: [queryKeys.DispatchEvent.Drafts] }),
      queryClient.refetchQueries({ queryKey: [queryKeys.DispatchEvent.Submissions] }),
    ]).then(() => {
      navigate('/');
    });
  };

  const handlePartnerNotification = (state: NotificationState) => {
    setNotifyPartner(state);
  };
  const handleUpdate = () => {
    update(formMethods.getValues());
  };

  const handleSubmit = () => {
    submit(dispatchEventReport.id, submitCallback);
  };

  const approveCallback = (eventReport: DispatchEventReport) => {
    // setting timeout for navigating allows refetch to finish
    // and display updated data in the approval list
    queryClient
      .refetchQueries({ queryKey: [queryKeys.DispatchEvent.ApprovalList] })
      .then(() => {
        navigate('/form/dispatch-event', {
          state: {
            isError: false,
            title: 'The report was successfully approved',
            message: `Occurrence number is ${eventReport.documentNumber}`,
          },
        });
      });
  };

  const approveFailCallback = (error: EventError) => enqueueSnackbar(error.message, { variant: 'error' });

  const handleApprove = () => {
    const request: IDispatchApprovalRequest = {
      reportId: dispatchEventReport.id,
      isConfidential: !(notifyPartner !== NotificationState.NotNotified && dispatchEventReport.mainlinePartner !== 'ZW'),
    };
    approve(request, approveCallback, approveFailCallback);
  };

  const handleDelete = () => {
    if (dispatchEventReport.id) {
      deleteDispatchEventDraft(dispatchEventReport.id, {
        onSuccess: () => {
          queryClient
            .refetchQueries({ queryKey: [queryKeys.DispatchEvent.ApprovalList] })
            .then(() => {
              navigate('/form/dispatch-event');
            });
        },
        onError: () => enqueueSnackbar('Error deleting form', { variant: 'error' }),
      });
    }
  };
  const openDeleteConfirmationDialog = () => {
    setConfDialogOpen(true);
  };

  return (
    <FormPaper color={authorizedFormData.metadata?.color}>
      <ScrollToTop />
      <ConfirmationDialog isOpen={confDialogOpen} setOpen={setConfDialogOpen} confirm={handleDelete} />
      <FormHeader>
        <FormTitle
          title="Dispatch Event Report"
          subtitle={[[
            'Reported By:',
            dispatchEventReport.employeeName,
            `[${dispatchEventReport.employeeId}],`,
            dispatchEventReport.employeeLocation,
            dispatchEventReport.employeeTitle,
          ].join(' ')]}
        />
        {!dispatchEventReport.confidential && <Box marginRight={1}><Chip color="info" label="Partner Notified" /></Box>}
        <FormStatus
          status={dispatchEventReport.status}
          subtext={undefined}
        />
      </FormHeader>
      <ControlledForm onUpdate={handleUpdate} enableUpdate>
        <FormProvider {...formMethods}>
          <FormValidationProvider<DispatchEventReport>>
            <Stack spacing={2}>
              <FormReadOnlySection title="Flight & Crew">
                <FlightStep visible readOnly />
              </FormReadOnlySection>
              <FormReadOnlySection title="Event">
                <EventStep visible readOnly />
              </FormReadOnlySection>
              <FormReadOnlySection title="Weather">
                <WeatherStep visible readOnly />
              </FormReadOnlySection>
              <FormReadOnlySection title="Narrative">
                <NarrativeStep visible readOnly />
              </FormReadOnlySection>
            </Stack>
          </FormValidationProvider>
        </FormProvider>
      </ControlledForm>
      <Box display="flex" sx={{ my: 2, justifyContent: 'flex-end' }}>
        <NotificationStateRadioGroup
          label="Notify American Airlines: "
          labelPlacement="start"
          disabled={NotificationState.AlreadyNotified === notifyPartner}
          hidden={!allowedActions?.approve || dispatchEventReport.mainlinePartner === 'ZW'}
          onChange={handlePartnerNotification}
          notificationFieldValue={!dispatchEventReport.confidential}
          defaultValue={NotificationState.Notify}
        />
      </Box>
      <Box display="flex" sx={{ my: 2 }}>
        <Button
          sx={{ marginRight: 2 }}
          onClick={() => navigate('/form/dispatch-event')}
        >
          Close
        </Button>
        {allowedActions?.delete && (
          <Button
            sx={{ marginRight: 2 }}
            color="error"
            variant="outlined"
            onClick={openDeleteConfirmationDialog}
          >
            Delete
          </Button>
        )}
        <Box flexGrow={1} />
        {allowedActions?.submit && (
          <LoadingButton
            loading={submitStatus === 'pending'}
            loadingPosition="end"
            endIcon={<SubmitIcon />}
            sx={{ marginRight: 2 }}
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={formMethods.formState.isDirty}
          >
            {`${formMethods.formState.isDirty ? 'Submit (Unsaved)' : 'Submit'}`}
          </LoadingButton>
        )}
        {allowedActions?.edit && (
          <Button
            sx={{ marginRight: 2 }}
            color="primary"
            variant="outlined"
            onClick={() => navigate(`/form/dispatch-event/${dispatchEventReport.id}/edit`)}
          >
            Edit
          </Button>
        )}
        {allowedActions?.approve && (
          <LoadingButton
            loading={approveStatus === 'pending'}
            loadingPosition="end"
            endIcon={<ApproveIcon />}
            sx={{ marginRight: 2 }}
            color="primary"
            variant="contained"
            onClick={handleApprove}
            disabled={formMethods.formState.isDirty}
          >
            {`${formMethods.formState.isDirty ? 'Approve (Unsaved)' : 'Approve'}`}
          </LoadingButton>
        )}
      </Box>
    </FormPaper>
  );
}

export default DispatchEventFormView;
